<template>
  <v-dialog :value="value" max-width="900" @input="$emit('input', $event)">
    <v-card>
      <v-toolbar color="primary" dark flat dense>
        <v-toolbar-title>{{
          $t("reports.aiClient.historyTitle")
        }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pt-5">
        <p class="grey--text mb-4">{{ $t("reports.aiClient.historyDesc") }}</p>

        <v-row class="mb-2">
          <v-col cols="12" md="4">
            <v-card outlined class="pa-4">
              <div class="black--text font-weight-bold mb-1">
                {{ $t("reports.aiClient.coopLabel") }}
              </div>
              <div class="text-h5 font-weight-bold black--text">
                {{ cooperative || "-" }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card outlined class="pa-4 cs-avail">
              <div class="black--text font-weight-bold mb-1">
                {{ $t("reports.aiClient.cardAvailable") }}
              </div>
              <div class="d-flex align-baseline" style="gap: 10px">
                <div class="text-h5 font-weight-bold black--text">
                  {{ formatNum(available) }}
                </div>
                <div class="grey--text text-caption" v-if="refreshesOn">
                  {{ $t("reports.aiClient.refreshesOn") }}:
                  {{ refreshesOn | formatDate }}
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-divider class="mb-3" />

        <div class="d-flex flex-wrap align-center justify-space-between mb-2">
          <v-text-field
            v-model="search"
            :placeholder="$t('reports.aiClient.search')"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            style="max-width: 320px"
          />
          <div class="d-flex align-center">
            <span class="grey--text mr-2">{{ rangeLabel }}</span>
            <v-btn icon :disabled="page <= 1" @click="prevPage"
              ><v-icon>mdi-chevron-left</v-icon></v-btn
            >
            <v-btn icon :disabled="!hasNextPage" @click="nextPage"
              ><v-icon>mdi-chevron-right</v-icon></v-btn
            >
          </div>
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredMonths"
          :loading="loading"
          hide-default-footer
          class="elevation-0"
          :no-data-text="$t('reports.aiClient.noHistory')"
        >
          <template v-slot:[`item.month`]="{ item }">{{
            formatMonth(item.month)
          }}</template>
          <template v-slot:[`item.allocation`]="{ item }">
            <span class="green--text text--darken-1"
              >+{{ formatNum(item.allocation) }}
              {{ $t("reports.aiClient.credits") }}</span
            >
          </template>
          <template v-slot:[`item.usage`]="{ item }">
            <span class="red--text"
              >-{{ formatNum(item.usage) }}
              {{ $t("reports.aiClient.credits") }}</span
            >
          </template>
          <template v-slot:[`item.closing`]="{ item }"
            >{{ formatNum(item.closing) }}
            {{ $t("reports.aiClient.credits") }}</template
          >
        </v-data-table>
      </v-card-text>

      <v-card-actions class="pb-4 px-4">
        <v-spacer />
        <v-btn color="primary" depressed @click="close">{{
          $t("reports.aiClient.close")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AiCreditService from "@/_services/AiCreditService";
import creditFormat from "@/mixins/creditFormat";

export default {
  name: "ClientCreditsHistoryDialog",
  mixins: [creditFormat],
  props: {
    value: { type: Boolean, default: false },
    cooperative: { type: String, default: "" },
  },
  data() {
    return {
      available: 0,
      refreshesOn: null,
      months: [],
      total: 0,
      page: 1,
      limit: 12,
      loading: false,
      search: "",
    };
  },
  computed: {
    headers() {
      const t = (k) => this.$t(`reports.aiClient.${k}`);
      return [
        { text: t("colMonth"), value: "month", sortable: false },
        { text: t("colCreditsAssigned"), value: "allocation", sortable: false },
        { text: t("colCreditsUsed"), value: "usage", sortable: false },
        { text: t("colClosingBalance"), value: "closing", sortable: false },
      ];
    },
    filteredMonths() {
      if (!this.search) return this.months;
      const q = this.search.toLowerCase();
      return this.months.filter((m) =>
        this.formatMonth(m.month).toLowerCase().includes(q)
      );
    },
    hasNextPage() {
      return this.page * this.limit < this.total;
    },
    rangeLabel() {
      const shown = Math.min(this.page * this.limit, this.total);
      return `${shown} ${this.$t("of")} ${this.total}`;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.page = 1;
        this.search = "";
        this.fetch();
      }
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    async fetch() {
      this.loading = true;
      try {
        const env = await AiCreditService.clientHistory({
          page: this.page,
          limit: this.limit,
        });
        const payload = (env && env.data) || {};
        this.available = payload.available || 0;
        this.refreshesOn = payload.refreshesOn || null;
        this.months = payload.months || [];
        this.total = (payload.pagination && payload.pagination.total) || 0;
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiClient.errorTitle"),
          text: this.$t("reports.aiClient.loadFailed"),
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1;
        this.fetch();
      }
    },
    nextPage() {
      if (this.hasNextPage) {
        this.page += 1;
        this.fetch();
      }
    },
    formatMonth(ym) {
      if (!ym) return "-";
      const [y, m] = ym.split("-");
      const names = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return `${names[Number(m) - 1] || m} ${y}`;
    },
  },
};
</script>

<style scoped>
.cs-avail {
  background: #f1faf4;
  border-color: #b7e3c9 !important;
}
</style>
